import { Action, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '..'
import {
  ActionError,
  DispatchError,
  FullScreenError,
  InlineError,
  RTKQueryResponse,
} from '../../../typings'
import { ERROR_CODES, ERROR_DISPLAY_TYPES } from '../../constants'
import { lookupErrorMessageByErrorCode } from '../../utils/errors/errorLookup'
import { GetContentFunction } from '../../hooks/useLocalizedContent'

type ServiceError = FullScreenError | InlineError | ActionError | DispatchError

const initialState: {
  statusCode?: number
  serviceError?: ServiceError
} = {
  statusCode: -1,
  serviceError: undefined,
}

const handledErrorSlice = createSlice({
  name: 'handledError',
  initialState,
  reducers: {
    setServiceError(state, action: PayloadAction<ServiceError>) {
      state.serviceError = action.payload
    },
    setStatusCode(state, action: PayloadAction<number>) {
      state.statusCode = action.payload
    },
    setCustomError(
      state,
      action: PayloadAction<InlineError | FullScreenError>,
    ) {
      state.serviceError = action.payload
    },
    clearHandledError(state) {
      state.serviceError = undefined
    },
  },
})

export const {
  setServiceError,
  setStatusCode,
  clearHandledError,
  setCustomError,
} = handledErrorSlice.actions
export default handledErrorSlice.reducer

const handleConnectionError = (errorCode: string, dispatch: AppDispatch) => {
  const serviceError = lookupErrorMessageByErrorCode(errorCode)

  dispatch(
    setServiceError({
      ...serviceError,
      redirect: null,
      getAction: null,
    }),
  )
}

export const setHandledError = (
  payload: RTKQueryResponse,
  getContent?: GetContentFunction,
) => {
  return (dispatch: AppDispatch): Action => {
    if (payload?.data) {
      const serviceError = lookupErrorMessageByErrorCode(
        payload?.data.error?.code,
      )
      dispatch(
        setServiceError({
          ...serviceError,
          // Remove functions that aren't serializable to the store
          redirect: null,
          getAction: null,
        }),
      )

      if (serviceError.type === ERROR_DISPLAY_TYPES.REDIRECT) {
        ;(serviceError as ActionError).redirect(payload.data)
      } else if (serviceError.type === ERROR_DISPLAY_TYPES.DISPATCH) {
        const actionToDispatch = (serviceError as DispatchError).getAction(
          payload?.data.error?.pool,
          getContent,
        )
        dispatch(actionToDispatch)
      }
    } else if (payload?.status?.toString() === ERROR_CODES.FETCH_ERROR) {
      handleConnectionError(payload.status.toString(), dispatch)
    } else {
      dispatch(setStatusCode(payload?.status))
    }

    // Return an empty action if no other action was dispatched inside the thunk
    return { type: 'handledError/setHandledError' } // Return an empty action
  }
}

export const selectHandledErrorState = (state: RootState) => state.handledError
export const selectServiceErrorState = (state: RootState) =>
  state.handledError.serviceError
